import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';

import { Box, TextField, InputAdornment, IconButton, Tooltip, Checkbox } from '@mui/material';
import { ArrowRightAlt, Cancel, CheckCircle, ReportProblem, ReportProblemOutlined } from '@mui/icons-material';

import { SCUIButton } from '../../../../../../atomic/atoms';
import { SCUICustomDataGrid } from '../../../../../../atomic/molecules';

import StorePickupPackageQuery from '../../../../../../services/query/storePickupPackage/StorePickupPackageQuery.services';
import StorePickupPackageCommand from '../../../../../../services/command/storePickupPackage/StorePickupPackageCommand.services';
import { useStorePickupPackageContext } from '../../../../context/StorePickupPackageContext';
import TimeHelper from '../../../../../../utils/time';
import Helper from '../../../../../../utils/helper';
import { SPP } from '../../../../storePickupPackage.classes';

import '../style.scss';

const markPackageAsMissingInList = (packageList: any[] | null, packageBarcode: string) =>
    (packageList || []).map((pkg: any) =>
        pkg.packageBarcode === packageBarcode
            ? {
                  ...pkg,
                  isMissing: true,
                  scanned: false,
                  scanTime: '',
                  damaged: false,
              }
            : pkg
    );

const removePackageByBarcode = (packages: any[], packageBarcode: string) =>
    packages.filter((pkg) => pkg.packageBarcode !== packageBarcode);

export const CargoAdmissionTab = ({ setCurrentStep }: any) => {
    const [barcodeText, setBarcodeText] = useState('');

    const { t } = useTranslation();
    const location = useLocation();
    const {
        cargoCompany,
        setPackageList,
        packageList,
        setDamagedRows,
        scannedRows,
        setScannedRows,
        setIsLoading,
        damagedRows,
        dPackageCargoShipmentCode,
    } = useStorePickupPackageContext();

    const handleDamagedCheckboxChange = (
        shipmentPackages: any[] | null,
        updateShipmentPackages: (updatedList: any[]) => void,
        packageBarcode: string,
        checked: boolean
    ) => {
        const updatedList = shipmentPackages ? [...shipmentPackages] : [];
        const index = updatedList.findIndex((pkg) => pkg.packageBarcode === packageBarcode);

        if (index !== -1) {
            updatedList[index] = {
                ...updatedList[index],
                damaged: checked,
            };

            const updatedPackage = updatedList[index];

            setDamagedRows((prev: any[]) => {
                if (checked) {
                    const alreadyExists = prev.some((row) => row.packageBarcode === packageBarcode);
                    return alreadyExists ? prev : [...prev, updatedPackage];
                } else {
                    return prev.filter((row) => row.packageBarcode !== packageBarcode);
                }
            });

            setScannedRows((prev: any[]) => {
                const alreadyExistsScanned = prev.some((row) => row.packageBarcode === packageBarcode);

                if (checked) {
                    // If marking as damaged, remove from scanned
                    return prev.filter((row) => row.packageBarcode !== packageBarcode);
                } else {
                    // If unchecking damaged, re-add to scanned if not already there
                    return alreadyExistsScanned ? prev : [...prev, updatedPackage];
                }
            });
        }

        updateShipmentPackages(updatedList);
    };

    const markPackageAsScanned = (packageList: any[] | null, barcodeText: any, scanTime: string) => {
        // Remove all non-numeric characters from barcodeText before comparison
        const numericBarcodeText = (barcodeText || '').replace(/\D/g, '');
        const index = (packageList || []).findIndex((pkg: any) => pkg.packageBarcode === numericBarcodeText);

        if (index !== -1) {
            const wasMissing = packageList?.[index]?.isMissing;
            const updatedItem = {
                ...packageList?.[index],
                scanned: true,
                scanTime,
                isMissing: false,
            };

            setScannedRows((prev: any[]) => {
                const alreadyExistsScanned = prev.some((row) => row.packageBarcode === numericBarcodeText);
                return alreadyExistsScanned ? prev : [...prev, updatedItem];
            });

            // Place the last scanned row at first
            const updatedList = [
                updatedItem,
                ...(packageList || []).slice(0, index),
                ...(packageList || []).slice(index + 1),
            ];

            Swal.fire({
                icon: 'success',
                title: wasMissing
                    ? t('storePickupPackageAdmission.scannedAfterMissing', {
                          defaultValue: 'Eksik işaretli paket okutuldu ve kabul listesine alındı',
                      })
                    : t('storePickupPackageAdmission.scannedSuccessfully'),
                showConfirmButton: false,
                timer: wasMissing ? 1500 : 500,
            });

            return updatedList;
        }

        return null;
    };

    const handleBarcode = async () => {
        const now = new Date();
        const scanTime = TimeHelper.formatDateHour(now);
        const updatedList = markPackageAsScanned(packageList, barcodeText, scanTime);

        if (updatedList !== null) {
            setPackageList(updatedList);
            setBarcodeText('');
        } else {
            try {
                setIsLoading(true);
                const response = await StorePickupPackageCommand.GenerateStorePickupMessage({
                    packageBarcode: barcodeText,
                    cultureCode: 'tr-TR',
                });

                if (response?.message) {
                    Swal.fire({
                        icon: 'error',
                        title: response.message,
                        showConfirmButton: true,
                        confirmButtonText: 'OK',
                    });
                } else {
                    Swal.fire({
                        icon: 'error',
                        title: t('storePickupPackageAdmission.scanFailed'),
                        showConfirmButton: true,
                        confirmButtonText: 'OK',
                    });
                }
                setIsLoading(false);
            } catch (error: any) {
                setIsLoading(false);
                const errorMessage = error?.data?.message || t('general.anErrorOccured');
                Swal.fire({
                    icon: 'error',
                    title: errorMessage,
                    showConfirmButton: true,
                    confirmButtonText: 'OK',
                });
            }
            setBarcodeText('');
        }
    };

    const clearGrid = () => {
        const resetList = (packageList || []).map((pkg: any) => ({
            ...pkg,
            damaged: false,
            scanned: false,
        }));
        setPackageList(resetList);
        setDamagedRows([]);
        setScannedRows([]);
    };

    const handleSubmit = () => {
        setCurrentStep(1);
    };

    const handleMarkMissing = async (row: any) => {
        const result = await Swal.fire({
            text: t('storePickupPackageAdmission.confirmMarkMissing'),
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.confirm'),
        });

        if (!result.isConfirmed) {
            return;
        }

        const storeCode = Helper.getStoreCode();
        const operatorId = Helper.getOperatorId();
        if (!storeCode || !operatorId) {
            Swal.fire(t('general.anErrorOccured'), '', 'error');
            return;
        }

        setIsLoading(true);
        try {
            await StorePickupPackageCommand.MarkDPackageShipmentMissing({
                dPackageCargoShipmentCode: row.dPackageCargoShipmentCode,
                storeCode,
                shipmentNumber: row.shipmentNumber,
                operatorId,
            });

            setPackageList(markPackageAsMissingInList(packageList, row.packageBarcode));
            setScannedRows((prev: any[]) => removePackageByBarcode(prev, row.packageBarcode));
            setDamagedRows((prev: any[]) => removePackageByBarcode(prev, row.packageBarcode));
            Swal.fire({
                icon: 'success',
                title: t('storePickupPackageAdmission.packageMarkedMissing'),
                showConfirmButton: false,
                timer: 1500,
            });
        } catch (error: any) {
            const errorMessage = error?.data?.message || t('general.anErrorOccured');
            Swal.fire({
                icon: 'error',
                title: errorMessage,
                showConfirmButton: true,
                confirmButtonText: 'OK',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const scannedCount = (scannedRows?.length || 0) + (damagedRows?.length || 0);
    const totalCount = packageList?.length || 0;

    const columns: any[] = [
        { field: 'id', headerName: 'id', flex: 1, hide: true },
        {
            field: 'isDamagedPackage',
            headerName: t('storePickupPackageAdmission.isDamagedPackage'),
            width: 110,
            minWidth: 100,
            flex: 0,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            cellClassName: SPP.cellCenter,
            renderCell: (params: any) => (
                <Checkbox
                    checked={params.row.damaged || false}
                    onChange={(e) =>
                        handleDamagedCheckboxChange(
                            packageList,
                            setPackageList,
                            params.row.packageBarcode,
                            e.target.checked
                        )
                    }
                    color="primary"
                    disabled={!params.row.scanned}
                    className={SPP.cellDamagedCheckbox}
                    data-package-barcode={params.row.packageBarcode}
                />
            ),
        },
        {
            field: 'packageBarcode',
            headerName: t('storePickupPackageAdmission.packageBarcode'),
            flex: 1,
            renderCell: (params: any) => (
                <span className={SPP.cellPackageBarcode} data-package-barcode={params.row.packageBarcode}>
                    {params.row.packageBarcode}
                </span>
            ),
        },
        {
            field: 'shipmentNumber',
            headerName: t('storePickupPackageAdmission.shippingNumber'),
            flex: 1,
            renderCell: (params: any) => {
                const shipmentNumber = params.row.shipmentNumber;

                if (!shipmentNumber) {
                    return (
                        <span className={SPP.cellShipmentNumber} data-shipment-number={shipmentNumber}>
                            {shipmentNumber}
                        </span>
                    );
                }

                return (
                    <a
                        target="_blank"
                        rel="noreferrer"
                        className={`${SPP.cellShipmentNumber} ${SPP.cellShipmentNumberLink}`}
                        data-shipment-number={shipmentNumber}
                        href={`/orderdetail?shippingListNo=${shipmentNumber}`}
                    >
                        {shipmentNumber}
                    </a>
                );
            },
        },
        {
            field: 'shippingCompany',
            headerName: t('storePickupPackageAdmission.cargoCompany'),
            flex: 1,
            renderCell: (params: any) => (
                <span className={SPP.cellShippingCompany} data-shipping-company={params.row.shippingCompany}>
                    {params.row.shippingCompany}
                </span>
            ),
        },
        {
            field: 'storeName',
            headerName: t('storePickupPackageAdmission.pickupStoreName'),
            flex: 1,
            renderCell: (params: any) => {
                const storeName = params.row.storeName || '';

                return (
                    <Tooltip title={storeName} placement="top" arrow>
                        <span className={SPP.cellStoreName} data-store-name={storeName}>
                            {storeName}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'storeCode',
            headerName: t('storePickupPackageAdmission.storeCode'),
            flex: 1,
            renderCell: (params: any) => (
                <span className={SPP.cellStoreCode} data-store-code={params.row.storeCode}>
                    {params.row.storeCode}
                </span>
            ),
        },
        {
            field: 'isDPackage',
            headerName: t('storePickupPackageAdmission.isDPackage'),
            width: 100,
            minWidth: 96,
            flex: 0,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            cellClassName: SPP.cellCenter,
            renderCell: (params: any) => {
                return params.value ? (
                    <Tooltip title="Success">
                        <CheckCircle
                            sx={{ fontSize: '24px' }}
                            color="success"
                            className={`${SPP.cellIsDPackage} ${SPP.cellIsDPackageOk}`}
                            data-is-d-package="true"
                        />
                    </Tooltip>
                ) : (
                    <Tooltip title="Failed">
                        <Cancel
                            sx={{ fontSize: '24px' }}
                            color="error"
                            className={`${SPP.cellIsDPackage} ${SPP.cellIsDPackageFail}`}
                            data-is-d-package="false"
                        />
                    </Tooltip>
                );
            },
        },
        {
            field: 'dPackageCargoShipmentCode',
            headerName: t('storePickupPackageAdmission.dPackageNo'),
            flex: 1,
            minWidth: 130,
            renderCell: (params: any) => (
                <span
                    className={SPP.cellDPackageNo}
                    data-d-package-no={params.row.dPackageCargoShipmentCode}
                >
                    {params.row.dPackageCargoShipmentCode || '-'}
                </span>
            ),
        },
        {
            field: 'isMissing',
            headerName: t('storePickupPackageAdmission.isMissing'),
            width: 80,
            minWidth: 72,
            flex: 0,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            disableColumnMenu: true,
            cellClassName: SPP.cellCenter,
            renderCell: (params: any) => {
                if (!params.row.isDPackage) {
                    return null;
                }

                const tooltipTitle = params.row.isMissing
                    ? t('storePickupPackageAdmission.packageMarkedMissing')
                    : t('storePickupPackageAdmission.markMissing');

                return (
                    <Tooltip title={tooltipTitle}>
                        <Box
                            component="span"
                            className={SPP.cellIsMissing}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%',
                            }}
                        >
                            <SCUIButton
                                text={tooltipTitle}
                                type="ghost"
                                sizeType="icon"
                                size="l"
                                buttonType="button"
                                disabled={params.row.isMissing}
                                onClick={() => handleMarkMissing(params.row)}
                                className={SPP.btnMarkMissing}
                            >
                                {params.row.isMissing ? (
                                    <ReportProblem sx={{ fontSize: 22, color: 'error.main' }} />
                                ) : (
                                    <ReportProblemOutlined sx={{ fontSize: 22, color: 'warning.main' }} />
                                )}
                            </SCUIButton>
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'scanTime',
            headerName: t('storePickupPackageAdmission.scanTime'),
            flex: 1,
            renderCell: (params: any) => (
                <span className={SPP.cellScanTime} data-scan-time={params.row.scanTime}>
                    {params.row.scanTime}
                </span>
            ),
        },
    ];

    const fetchPackageList = () => {
        if (!Helper.getStoreCode()) {
            setPackageList(null);
            return;
        }

        setIsLoading(true);
        StorePickupPackageQuery.GetStorePickupPackageList({
            shippingCompanyId: cargoCompany,
            storeCode: Helper.getStoreCode(),
            dPackageCargoShipmentCode: dPackageCargoShipmentCode || undefined,
        })
            .then((res: any) => {
                if (res.data) {
                    setPackageList(res.data || []);
                }
                setIsLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
                Swal.fire(t('general.anErrorOccured'), '', 'error');
            });
    };

    useEffect(() => {
        if (packageList === null) {
            fetchPackageList();
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab === '0') {
            setPackageList([]);
            setScannedRows([]);
            setDamagedRows([]);
            fetchPackageList();
        }
    }, [location.search]);

    return (
        <Box className={SPP.admissionInner}>
            <TextField
                label={t('storePickupPackageAdmission.packageBarcode')}
                className={SPP.admissionBarcodeInput}
                sx={{
                    width: '250px',
                    marginBottom: '24px',
                    '& .MuiInputBase-root': {
                        paddingRight: '0 !important',
                    },
                    '& .MuiIconButton-root': {
                        borderRadius: '4px',
                        height: '34px',
                        width: '32px',
                        border: 'none',

                        '&:hover': {
                            background: '#03a9f4',
                        },
                    },
                }}
                value={barcodeText || ''}
                onChange={(e: any) => setBarcodeText(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                        handleBarcode();
                    }
                }}
                slotProps={{
                    input: {
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    sx={{ backgroundColor: '#03a9f4', borderRadius: '0' }}
                                    onClick={handleBarcode}
                                    className={SPP.admissionBarcodeSubmit}
                                >
                                    <ArrowRightAlt sx={{ fill: '#fff' }} />
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
            />
            <Box className={SPP.admissionGrid}>
                <SCUICustomDataGrid
                    rows={packageList || []}
                    columns={columns}
                    sx={{
                        '& .MuiDataGrid-cell[data-field="isMissing"]': {
                            justifyContent: 'center',
                            padding: 0,
                        },
                        '& .MuiDataGrid-cell[data-field="isDPackage"]': {
                            justifyContent: 'center',
                        },
                        '& .MuiDataGrid-cell[data-field="isDamagedPackage"]': {
                            justifyContent: 'center',
                        },
                    }}
                    getRowClassName={(params: any) => {
                        if (params.row.isMissing) return 'missing-row';
                        if (params.row.scanned) return 'barcode-scanned';
                        if (params.row.damaged) return 'damaged-row';
                        return '';
                    }}
                    hideFooter
                    allrecord
                />
            </Box>
            <Box
                className={SPP.admissionTotalRow}
                sx={{ width: '100%', marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}
            >
                <p
                    className={`${SPP.admissionTotalAmount} ${SPP.admissionScannedCounter}`}
                    style={{
                        fontSize: '16px',
                        fontWeight: '700',
                        margin: 0,
                        padding: '8px 16px',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '4px',
                        border: '1px solid #2196f3',
                    }}
                >
                    {t('storePickupPackageAdmission.scannedPackageCount', {
                        scanned: scannedCount,
                        total: totalCount,
                    })}
                </p>
            </Box>
            {(scannedRows?.length > 0 || damagedRows?.length > 0) && (
                <Box
                    className={SPP.admissionActions}
                    sx={{ display: 'flex', flexDirection: 'row', width: '100%', marginTop: '24px' }}
                >
                    <SCUIButton
                        text={t('general.clear')}
                        style={{ marginRight: 'auto', backgroundColor: 'red', borderColor: 'red' }}
                        type="primary"
                        sizeType="fixed"
                        buttonType="button"
                        size={'m'}
                        onClick={clearGrid}
                        className={SPP.btnClear}
                    />
                    <SCUIButton
                        text={t('general.continue')}
                        style={{ marginLeft: 'auto' }}
                        type="primary"
                        sizeType="fixed"
                        buttonType="button"
                        size={'m'}
                        onClick={handleSubmit}
                        className={SPP.btnContinue}
                    />
                </Box>
            )}
        </Box>
    );
};
